import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Search, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { listProcessSkillOptions } from '@/api/processes-api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ProvenanceAttribution } from '@/components/work4me/ProvenanceAttribution';
import { Work4MeSkillCreate } from './Work4MeSkillCreate';

export function Work4MeSkillList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [showNewSkillDialog, setShowNewSkillDialog] = useState(false);
  const skillOptionsQuery = useQuery({
    queryKey: ['process-skill-options'],
    queryFn: listProcessSkillOptions,
  });
  const filteredSkills = useMemo(() => {
    const skills = skillOptionsQuery.data?.items ?? [];
    const term = search.trim().toLowerCase();
    if (!term) {
      return skills;
    }
    return skills.filter((skill) =>
      skill.name.toLowerCase().includes(term)
      || (skill.description ?? '').toLowerCase().includes(term)
      || skill.id.toString().includes(term)
    );
  }, [search, skillOptionsQuery.data]);

  return (
    <div className="-mx-4 py-2">
      <div className="px-4 py-3 border-b bg-muted/20">
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-xl font-light tracking-wide">Skills</h2>
            <div className="flex items-center gap-2 flex-wrap justify-end">
            <div className="relative w-[240px] sm:w-[280px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search skills..."
                className="h-9 pl-10"
              />
            </div>
            <Button onClick={() => setShowNewSkillDialog(true)} className="gap-2 h-9 px-3">
              <Plus className="h-4 w-4" />
              Skill
            </Button>
          </div>
          </div>
          <p className="text-muted-foreground">
            View enabled skills available to runtime and process execution in this workspace.
          </p>
        </div>
      </div>

      {skillOptionsQuery.isLoading ? (
        <div className="px-4 py-6 text-sm text-muted-foreground">Loading skills...</div>
      ) : null}
      {skillOptionsQuery.isError ? (
        <div className="px-4 py-6 text-sm text-destructive">Unable to load skills.</div>
      ) : null}
      {!skillOptionsQuery.isLoading && !skillOptionsQuery.isError && filteredSkills.length === 0 ? (
        <div className="px-4 py-6 text-sm text-muted-foreground">
          {search.trim() ? 'No skills match your search.' : 'Nothing here yet — automations you create appear here.'}
        </div>
      ) : null}

      {!skillOptionsQuery.isLoading && !skillOptionsQuery.isError && filteredSkills.length > 0 ? (
        <ul className="divide-y">
          {filteredSkills.map((skill) => (
            <li
              key={skill.id}
              data-testid="work4me-skill-entry"
              className="flex items-center gap-2 px-4 py-3 hover:bg-muted/30 transition-colors cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/work4me/skills/${skill.id}`)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  navigate(`/work4me/skills/${skill.id}`);
                }
              }}
              aria-label={`Open ${skill.name}`}
            >
              <Sparkles className="h-4 w-4 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium truncate">{skill.name}</p>
                  <Badge variant="outline">{skill.ownerScope}</Badge>
                </div>
                <p className="text-xs text-muted-foreground truncate">{skill.description ?? 'No description'}</p>
                <ProvenanceAttribution lastChange={skill.lastChange} />
              </div>
              <Badge variant="secondary" className="text-[10px]">ID {skill.id}</Badge>
            </li>
          ))}
        </ul>
      ) : null}

      <Work4MeSkillCreate
        open={showNewSkillDialog}
        onOpenChange={setShowNewSkillDialog}
        onCreated={(skillId) => navigate(`/work4me/skills/${skillId}`)}
      />
    </div>
  );
}

